import { isProviderVisibleAtLocation } from "@/components/provider-select/provider-location-visibility";

import {
  addonEntryKey,
  flattenServiceAssignmentEntries,
  occurrenceKey,
} from "@/lib/service-addon-utils";

import type {
  AvailabilityRequestAssignment,
  BookingPreferencesV1,
  FrontPage,
} from "@/types/self-schedule";
import type { PublicServiceDetail } from "@/types/service";

import {
  FIRST_AVAILABLE_PROVIDER_ID,
  type SelectedServiceInstance,
  generateInstanceId,
} from "@/stores/self-schedule-store";

export interface BookingPreferencesDraft {
  selectedLocationId: string | null;
  selectedServiceInstances: SelectedServiceInstance[];
  selectedAddons: Record<string, string[]>;
  serviceInstanceProviderIds: Record<string, string>;
  addonProviderIds: Record<string, string>;
  preferredProviderEntryKeys: Record<string, string>;
  droppedChoices: boolean;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);
const isId = (value: unknown): value is string =>
  typeof value === "string" && value.trim().length > 0;

/** Validate the authenticated API response before applying booking choices. */
export function validateBookingPreferences(
  value: unknown,
): BookingPreferencesV1 | null {
  if (
    !isRecord(value) ||
    Object.keys(value).some(
      (key) =>
        !["version", "locationId", "assignments", "addonSelections"].includes(
          key,
        ),
    ) ||
    value.version !== 1 ||
    !(value.locationId === null || isId(value.locationId)) ||
    !Array.isArray(value.assignments) ||
    value.assignments.length === 0 ||
    !isRecord(value.addonSelections)
  )
    return null;
  if (
    !value.assignments.every((entry: unknown, index: number) => {
      if (
        !isRecord(entry) ||
        Object.keys(entry).some(
          (key) =>
            ![
              "serviceId",
              "providerId",
              "preferredProviderAssignmentIndex",
            ].includes(key),
        ) ||
        !isId(entry.serviceId) ||
        !(entry.providerId === null || isId(entry.providerId))
      )
        return false;
      const anchor = entry.preferredProviderAssignmentIndex;
      return (
        anchor == null ||
        (Number.isInteger(anchor) &&
          typeof anchor === "number" &&
          anchor >= 0 &&
          anchor < index &&
          entry.providerId === null)
      );
    })
  )
    return null;
  if (
    !Object.entries(value.addonSelections).every(
      ([key, ids]) =>
        /^.+:[1-9]\d*$/.test(key) && Array.isArray(ids) && ids.every(isId),
    )
  )
    return null;
  return value as unknown as BookingPreferencesV1;
}

/** Decode the flat parent/child wire walk before dropping stale catalog choices. */
export function normalizeBookingPreferences(
  preferences: BookingPreferencesV1,
  catalog: FrontPage,
  maxParents: number,
): BookingPreferencesDraft | null {
  const services = new Map(
    catalog.services.map((service) => [service.id, service]),
  );
  const location = catalog.locations.find(
    (candidate) =>
      candidate.id === preferences.locationId && !candidate.isArchived,
  );
  const draft: BookingPreferencesDraft = {
    selectedLocationId: location?.id ?? null,
    selectedServiceInstances: [],
    selectedAddons: {},
    serviceInstanceProviderIds: {},
    addonProviderIds: {},
    preferredProviderEntryKeys: {},
    droppedChoices: preferences.locationId !== null && !location,
  };
  const counts = new Map<string, number>();
  const retainedCounts = new Map<string, number>();
  const entryKeys = new Map<number, string>();
  const consumedSelections = new Set<string>();
  const eligibleProvider = (
    service: PublicServiceDetail,
    providerId: string | null,
  ) => {
    if (providerId === null) return FIRST_AVAILABLE_PROVIDER_ID;
    const eligible = computeEligibleProviderIds(
      service,
      catalog,
      draft.selectedLocationId,
    );
    if (
      !service.allowIndividualProviderBooking ||
      !eligible.includes(providerId)
    ) {
      draft.droppedChoices = true;
      return FIRST_AVAILABLE_PROVIDER_ID;
    }
    return providerId;
  };
  for (let cursor = 0; cursor < preferences.assignments.length; ) {
    const parentIndex = cursor;
    const assignment = preferences.assignments[cursor++];
    const count = (counts.get(assignment.serviceId) ?? 0) + 1;
    counts.set(assignment.serviceId, count);
    const key = occurrenceKey(assignment.serviceId, count);
    consumedSelections.add(key);
    const mappingIds = preferences.addonSelections[key] ?? [];
    const service = services.get(assignment.serviceId);
    const children = mappingIds.map((mappingId) => {
      const childIndex = cursor++;
      const child = preferences.assignments[childIndex];
      const mapping = service?.addons?.find((addon) => addon.id === mappingId);
      return { mappingId, mapping, child, childIndex };
    });
    // A missing catalog mapping can be recovered by consuming its wire child;
    // an extant mapping pointing at a different child is a malformed payload.
    if (
      children.some(
        ({ child, mapping }) =>
          !child || (mapping && child.serviceId !== mapping.addonService.id),
      )
    )
      return null;
    const retainedCount = retainedCounts.get(assignment.serviceId) ?? 0;
    // A truncated waitlist request cannot reconcile with its original identity.
    // Keep the tenant cap and present the existing unavailable-link state.
    if (draft.selectedServiceInstances.length >= maxParents) return null;
    if (
      !service ||
      service.onlyBookableOverThePhone ||
      (retainedCount > 0 && !service.allowMultipleBookings) ||
      (draft.selectedLocationId &&
        service.locationIds?.length &&
        !service.locationIds.includes(draft.selectedLocationId))
    ) {
      draft.droppedChoices = true;
      continue;
    }
    const instanceId = generateInstanceId();
    retainedCounts.set(service.id, retainedCount + 1);
    draft.selectedServiceInstances.push({
      id: service.id,
      instanceId,
      name: service.name,
    });
    draft.serviceInstanceProviderIds[instanceId] = eligibleProvider(
      service,
      assignment.providerId,
    );
    entryKeys.set(parentIndex, instanceId);
    const retainedMappingIds: string[] = [];
    const mappingOrdinals = new Map<string, number>();
    for (const { mappingId, mapping, child, childIndex } of children) {
      if (!mapping) {
        draft.droppedChoices = true;
        continue;
      }
      retainedMappingIds.push(mappingId);
      const ordinal = (mappingOrdinals.get(mappingId) ?? 0) + 1;
      mappingOrdinals.set(mappingId, ordinal);
      const childKey = addonEntryKey(instanceId, mappingId, ordinal);
      entryKeys.set(childIndex, childKey);
      // This is an aligned saved addon assignment, not a public provider
      // chooser. Addon-only children need not appear in the standalone
      // catalog; public availability validates their concrete providers.
      draft.addonProviderIds[childKey] =
        child.providerId ?? FIRST_AVAILABLE_PROVIDER_ID;
    }
    draft.selectedAddons[occurrenceKey(service.id, retainedCount + 1)] =
      retainedMappingIds;
  }
  if (
    Object.keys(preferences.addonSelections).some(
      (key) => !consumedSelections.has(key),
    )
  )
    return null;
  preferences.assignments.forEach((assignment, index) => {
    const source = entryKeys.get(index);
    const target =
      assignment.preferredProviderAssignmentIndex == null
        ? undefined
        : entryKeys.get(assignment.preferredProviderAssignmentIndex);
    if (source && target && assignment.providerId === null)
      draft.preferredProviderEntryKeys[source] = target;
    else if (source && assignment.preferredProviderAssignmentIndex != null)
      draft.droppedChoices = true;
  });
  return draft;
}

export function computeEligibleProviderIds(
  service: PublicServiceDetail,
  catalog: FrontPage,
  locationId: string | null,
): string[] {
  const ids = service.effectiveProviderIds ?? service.providerIds ?? [];
  return catalog.providers
    .filter(
      (provider) =>
        ids.includes(provider.id) &&
        isProviderVisibleAtLocation(provider.visibleLocationIds, locationId),
    )
    .map((provider) => provider.id);
}

/** Derive numeric backward indices from current editable instance identities. */
export function buildBookingPreferenceAssignments(
  instances: SelectedServiceInstance[],
  services: PublicServiceDetail[],
  addons: Record<string, string[]>,
  parentProviders: Record<string, string>,
  childProviders: Record<string, string>,
  anchors: Record<string, string>,
): AvailabilityRequestAssignment[] {
  const entries = flattenServiceAssignmentEntries(
    instances,
    new Map(services.map((service) => [service.id, service])),
    addons,
    parentProviders,
  );
  const indices = new Map(
    entries.map((entry, index) => [entry.entryKey, index]),
  );
  return entries.map(({ assignment, entryKey, sourceInstanceId }, index) => {
    const isChild = entryKey !== sourceInstanceId;
    const preference = isChild
      ? childProviders[entryKey]
      : assignment.providerId;
    const providerId =
      preference && preference !== FIRST_AVAILABLE_PROVIDER_ID
        ? preference
        : null;
    // Only newly selected children (no stored preference) default to their parent.
    const anchor =
      anchors[entryKey] ??
      (isChild && preference === undefined ? sourceInstanceId : undefined);
    const anchorIndex = anchor === undefined ? undefined : indices.get(anchor);
    return {
      serviceId: assignment.serviceId,
      providerId,
      ...(providerId === null &&
      anchorIndex !== undefined &&
      anchorIndex < index
        ? { preferredProviderAssignmentIndex: anchorIndex }
        : {}),
    };
  });
}
